/**
 * @file bannerLink.js
 * @description 首页 Banner 格位漫画展示与跳转
 * @module features/home/utils/bannerLink
 */

import { convertRouteParams } from '@/shared/utils/idHelper'
import { isMobileLayoutViewport } from '@/shared/utils/mobileLayout'
import { getOssImageUrl } from '@/shared/utils/ossImage'

/**
 * @typedef {import('../api/homeApi').HomeBannerComic} HomeBannerComic
 */

/** 轮播区域格位总数 */
export const BANNER_SLOT_COUNT = 8

/**
 * 可点击轮播区域占视口宽度比例（B站参考：1440px 屏宽 → 931px 可点区域）
 */
export const BANNER_ACTIVE_WIDTH_RATIO = 931 / 1440

/** 格位序号 1~8（从左到右、从上到下） */
export const BANNER_POSITIONS = Array.from(
  { length: BANNER_SLOT_COUNT },
  (_, i) => i + 1
)

/** 主焦点格位（居中大图，展示标题与分页点） */
export const BANNER_MAIN_POSITION = 4

/** H5 全宽单图格位 */
export const BANNER_H5_POSITION = 1

/**
 * @typedef {'WEB' | 'H5'} HomeBannerClientType
 */

/**
 * 按当前视口解析 Banner 请求端类型
 * @returns {HomeBannerClientType}
 */
export function resolveBannerClientType() {
  if (typeof window === 'undefined') return 'WEB'
  return isMobileLayoutViewport() ? 'H5' : 'WEB'
}

/**
 * @typedef {Object} BannerImageUrlOptions
 * @property {number} [position] 格位序号 1~8
 * @property {'web' | 'h5'} [layoutMode='web']
 */

/**
 * 解析格位封面 URL（完整地址 + 按展示位 OSS 等比缩放）
 * @param {HomeBannerComic|null|undefined} comic
 * @param {BannerImageUrlOptions} [options]
 * @returns {string}
 */
export function getBannerImageUrl(comic, options = {}) {
  const url = comic?.coverUrl ?? ''
  if (!url) return ''

  const { position, layoutMode = 'web' } = options
  if (layoutMode === 'h5') {
    return getOssImageUrl(url, 'bannerMain')
  }
  if (position === BANNER_MAIN_POSITION) {
    return getOssImageUrl(url, 'bannerMain')
  }
  return getOssImageUrl(url, 'bannerSide')
}

/**
 * 解析格位展示标题
 * @param {HomeBannerComic|null|undefined} comic
 * @returns {string}
 */
export function getBannerTitle(comic) {
  return comic?.name ?? ''
}

/** 8 宫格列宽（fr），格位 1 / 4 同宽 */
export const BANNER_GRID_COLUMN_FR = [1.2, 0.72, 1.2, 0.72, 0.72]

/** 列宽总和，用于预览裁切比例 */
export const BANNER_GRID_COLUMN_FR_SUM = BANNER_GRID_COLUMN_FR.reduce(
  (sum, fr) => sum + fr,
  0
)

/** 左侧预览裁切：保留上一屏格位 5~8（第 4、5 列） */
export const BANNER_PREVIEW_LEFT_CLIP_RATIO =
  (BANNER_GRID_COLUMN_FR[0] + BANNER_GRID_COLUMN_FR[1] + BANNER_GRID_COLUMN_FR[2]) /
  BANNER_GRID_COLUMN_FR_SUM

/** 右侧预览裁切：保留下一屏格位 1（第 1 列） */
export const BANNER_PREVIEW_RIGHT_CLIP_RATIO =
  BANNER_GRID_COLUMN_FR[0] / BANNER_GRID_COLUMN_FR_SUM

/**
 * @param {number} count
 * @param {number} index
 */
export function getCircularPrevIndex(count, index) {
  if (count <= 0) return 0
  return (index - 1 + count) % count
}

/**
 * @param {number} count
 * @param {number} index
 */
export function getCircularNextIndex(count, index) {
  if (count <= 0) return 0
  return (index + 1) % count
}

/**
 * 从右往左（下一屏）所需步数
 * @param {number} from
 * @param {number} to
 * @param {number} count
 */
export function getForwardSlideSteps(from, to, count) {
  if (count <= 0) return 0
  return (to - from + count) % count
}

/**
 * 从左往右（上一屏）所需步数
 * @param {number} from
 * @param {number} to
 * @param {number} count
 */
export function getBackwardSlideSteps(from, to, count) {
  if (count <= 0) return 0
  return (from - to + count) % count
}

/**
 * 轨道头部克隆屏数量（含左侧预览克隆）
 * @param {number} count
 */
export function getTrackLeadingCloneCount(count) {
  return count > 1 ? 2 : 0
}

/**
 * 真实屏下标 → 轨道索引
 * @param {number} slideIndex
 * @param {number} count
 */
export function toBannerTrackIndex(slideIndex, count) {
  if (count <= 1) return slideIndex
  return slideIndex + getTrackLeadingCloneCount(count)
}

/**
 * 末屏→首屏循环：动画终点轨道索引（首屏尾部克隆）
 * @param {number} count
 */
export function getForwardLoopTrackIndex(count) {
  return toBannerTrackIndex(0, count) + count
}

/**
 * 首屏→末屏循环：动画终点轨道索引（末屏头部克隆）
 */
export function getBackwardLoopTrackIndex() {
  return 1
}

/**
 * 选择更短路径的切屏方向
 * @param {number} from
 * @param {number} to
 * @param {number} count
 * @returns {'forward' | 'backward' | 'none'}
 */
export function resolveSlideDirection(from, to, count) {
  const forward = getForwardSlideSteps(from, to, count)
  const backward = getBackwardSlideSteps(from, to, count)
  if (forward === 0) return 'none'
  if (backward < forward) return 'backward'
  return 'forward'
}

/**
 * 将一屏 comics 按 position 映射为 1~8 格位
 * @param {HomeBannerComic[]} comics
 * @returns {Record<number, HomeBannerComic|null>}
 */
export function buildBannerSlotMap(comics) {
  /** @type {Record<number, HomeBannerComic|null>} */
  const slots = {}
  for (let pos = 1; pos <= BANNER_SLOT_COUNT; pos++) {
    slots[pos] = null
  }
  for (const comic of comics) {
    const pos = Number(comic.position)
    if (pos >= 1 && pos <= BANNER_SLOT_COUNT) {
      slots[pos] = comic
    }
  }
  return slots
}

/**
 * 构建 Banner 轨道屏列表（含首尾克隆）
 * 尾部额外克隆第 2 屏，使末屏→首屏循环衔接时两侧预览与首屏静止态一致
 * @param {Record<number, HomeBannerComic|null>[]} slotMaps
 * @returns {Array<{ slotMap: Record<number, HomeBannerComic|null>, slideIndex: number, key: string, isClone: boolean }>}
 */
export function buildBannerTrackSlides(slotMaps) {
  const count = slotMaps.length
  if (count <= 1) {
    return slotMaps.map((slotMap, index) => ({
      slotMap,
      slideIndex: index,
      key: `real-${index}`,
      isClone: false,
    }))
  }

  /** @type {Array<{ slotMap: Record<number, HomeBannerComic|null>, slideIndex: number, key: string, isClone: boolean }>} */
  const slides = []

  if (count > 1) {
    const peekIndex = count > 2 ? count - 2 : 0
    slides.push({
      slotMap: slotMaps[peekIndex],
      slideIndex: peekIndex,
      key: 'clone-prev-peek',
      isClone: true,
    })
    slides.push({
      slotMap: slotMaps[count - 1],
      slideIndex: count - 1,
      key: 'clone-prev',
      isClone: true,
    })
  }

  slides.push(
    ...slotMaps.map((slotMap, index) => ({
      slotMap,
      slideIndex: index,
      key: `real-${index}`,
      isClone: false,
    }))
  )

  if (count > 1) {
    slides.push(
      {
        slotMap: slotMaps[0],
        slideIndex: 0,
        key: 'clone-next',
        isClone: true,
      },
      {
        slotMap: slotMaps[1],
        slideIndex: 1,
        key: 'clone-next-peek',
        isClone: true,
      }
    )
  }

  return slides
}

/**
 * 跳转漫画详情
 * @param {import('vue-router').Router} router
 * @param {HomeBannerComic|null|undefined} comic
 */
export function navigateBannerComic(router, comic) {
  if (!comic?.comicId) return
  router.push({
    name: 'ComicDetail',
    params: convertRouteParams({ id: comic.comicId }),
  })
}

/** @type {Set<string>} 已预加载的封面 URL，避免重复发起请求 */
const preloadedCoverUrls = new Set()

/**
 * 预加载首屏 Banner 封面（仅第 1 屏，避免与 DOM 内全量 eager 重复请求）
 * @param {import('../api/homeApi').HomeBannerFrame[]} frames
 * @param {'WEB' | 'H5'} [clientType='WEB']
 */
export function preloadBannerCoverUrls(frames, clientType = 'WEB') {
  if (typeof window === 'undefined' || !Array.isArray(frames) || frames.length === 0) {
    return
  }

  const layoutMode = clientType === 'H5' ? 'h5' : 'web'
  const firstFrame = frames[0]
  const comics = firstFrame?.comics ?? []

  for (const comic of comics) {
    const position = Number(comic.position)
    const url = getBannerImageUrl(comic, { position, layoutMode })
    if (!url || preloadedCoverUrls.has(url)) continue
    preloadedCoverUrls.add(url)
    const img = new Image()
    img.decoding = 'async'
    if (position === BANNER_MAIN_POSITION || layoutMode === 'h5') {
      img.fetchPriority = 'high'
    }
    img.src = url
  }
}
